import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api';
import Loader from '../components/Loader';
import styles from './PropertyNoticeToQuitGuardianLetters.module.css';

/**
 * Property Notice to Quit - Guardian Letters List
 * Laravel equivalent: RentNoticeToQuitLetterController@displayListOfProperty
 * View: properties/notice_to_quit/guardian_letters/list.blade.php
 */
const PropertyNoticeToQuitGuardianLetters = () => {
  const { id } = useParams(); // property ID
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [rentNTQLetters, setRentNTQLetters] = useState([]);
  const [selectedLetters, setSelectedLetters] = useState({});
  const [mergingLetters, setMergingLetters] = useState(false);

  useEffect(() => {
    loadGuardianLetters();
  }, [id]);

  const loadGuardianLetters = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/properties/${id}/notice-to-quit/guardian-letters`);

      if (response.data.success) {
        setProperty(response.data.data.property);
        setRentNTQLetters(response.data.data.rentNTQLetters || []);

        // Pre-select all letters for merging (Laravel default behavior)
        const selected = {};
        (response.data.data.rentNTQLetters || []).forEach(letter => {
          if (letter.letter_url) {
            selected[letter.id] = true;
          }
        });
        setSelectedLetters(selected);
      } else {
        toast.error(response.data.message || 'Failed to load guardian letters');
      }
    } catch (error) {
      console.error('Error loading guardian letters:', error);
      toast.error(error.response?.data?.message || 'Failed to load guardian letters');
      navigate(`/properties/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (letterId) => {
    setSelectedLetters(prev => ({
      ...prev,
      [letterId]: !prev[letterId]
    }));
  };

  const handleOpenMerged = async (e) => {
    e.preventDefault();

    const selectedIds = Object.keys(selectedLetters).filter(id => selectedLetters[id]);

    if (selectedIds.length === 0) {
      toast.error('Please select at least one letter to merge');
      return;
    }

    try {
      setMergingLetters(true);

      const response = await api.post(`/properties/${id}/notice-to-quit/open-merged`, {
        to_merge: selectedIds
      }, {
        responseType: 'blob'
      });

      // Create blob URL and open in new tab
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      toast.success('Merged PDF opened successfully');
    } catch (error) {
      console.error('Error merging letters:', error);
      toast.error(error.response?.data?.message || 'Failed to merge letters');
    } finally {
      setMergingLetters(false);
    }
  };

  const handleDownloadLetter = async (letterId, guardianName) => {
    try {
      const response = await api.get(`/properties/${id}/notice-to-quit/guardian-letters/${letterId}/download`, {
        responseType: 'blob'
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NTQ_Letter_${guardianName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Letter downloaded successfully');
    } catch (error) {
      console.error('Error downloading letter:', error);
      toast.error('Failed to download letter');
    }
  };

  const handleOpenLetter = async (letterId) => {
    try {
      const response = await api.get(`/properties/${id}/notice-to-quit/guardian-letters/${letterId}/view`, {
        responseType: 'blob'
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening letter:', error);
      toast.error('Failed to open letter');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className={styles.loadingContainer}>
          <Loader />
          <p>Loading guardian letters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumbs */}
      <ol className="breadcrumb">
        <li><a href="/dashboard">Dashboard</a></li>
        <li><a href="/properties">Properties</a></li>
        <li><a href={`/properties/${id}`}>{property?.name || 'Property'}</a></li>
        <li className="active">Guardian Letters</li>
      </ol>

      <div className="row">
        <div className="col-xs-12">
          <div className="panel-title title-standard">
            <h3>Guardian Letters</h3>
          </div>
          <div className="panel-group border-down margin-down">

            {rentNTQLetters.length > 0 ? (
              <form onSubmit={handleOpenMerged}>
                <table className="table table-condensed">
                  <thead>
                    <tr>
                      <th>Guardian</th>
                      <th>UGD</th>
                      <th>Actions</th>
                      <th className="text-center">Merge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rentNTQLetters.map((letter) => {
                      // Only show letters that have a generated letter file
                      if (!letter.letter_url && !letter.has_letter) {
                        return null;
                      }

                      return (
                        <tr key={letter.id}>
                          <td>
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/guardians/${letter.guardian_id}`);
                              }}
                            >
                              {letter.guardian_name || 'Guardian'}
                            </a>
                          </td>
                          <td>{letter.unique_guardian_number || 'N/A'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-info"
                              onClick={() => handleDownloadLetter(letter.id, letter.guardian_name)}
                              style={{ marginRight: '5px' }}
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-info"
                              onClick={() => handleOpenLetter(letter.id)}
                            >
                              Open
                            </button>
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={selectedLetters[letter.id] || false}
                              onChange={() => handleCheckboxChange(letter.id)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="pull-right">
                  <button
                    type="submit"
                    className="btn btn-head btn-standard pull-right"
                    disabled={mergingLetters}
                  >
                    {mergingLetters ? (
                      <>
                        <i className="fa fa-spinner fa-spin"></i> Merging...
                      </>
                    ) : (
                      'Open merged'
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <p>No guardian lived there. There is no guardian letter.</p>
            )}

          </div>
        </div>
      </div>

      {/* Back button */}
      <div className="row" style={{ marginTop: '20px' }}>
        <div className="col-xs-12">
          <button
            className="btn btn-default"
            onClick={() => navigate(`/properties/${id}`)}
          >
            <i className="glyphicon glyphicon-arrow-left"></i> Back to Property
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyNoticeToQuitGuardianLetters;
